import { useState, useEffect } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { sessionService } from '../../services';
import toast from 'react-hot-toast';

/**
 * Reschedule a session — pick a new date and start time. End time is computed
 * from the session's existing duration (caller can override via the duration
 * field if needed). Hits PUT /api/sessions/:id/reschedule which also flips
 * status from 'expired' → 'scheduled' atomically.
 *
 * Props:
 *   isOpen      — open/close state
 *   onClose     — close callback
 *   session     — the session object (needs _id, scheduledDate, startTime, duration, name, code)
 *   onSuccess   — called with the updated session payload after a successful save
 */
const RescheduleSessionModal = ({ isOpen, onClose, session, onSuccess }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  // Optional explicit end time. Left blank the server derives it from duration,
  // which is the common case; set, it wins. Needed because a session whose stored
  // endTime is wrong (earlier than its start, so permanently "expired") could not be
  // repaired from this dialog at all — the only editable field was the start.
  const [endTimeValue, setEndTimeValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill with the next reasonable slot whenever the session changes.
  // For an expired session, defaulting to "tomorrow at the original time"
  // gets the user 90% of the way there in one click.
  useEffect(() => {
    if (!isOpen || !session) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDate(tomorrow.toISOString().slice(0, 10));
    // Blank means "derive from duration" — don't seed it from the stored endTime,
    // which may be the very value the user opened this dialog to correct.
    setEndTimeValue('');

    const startSrc = session.startTime || session.scheduledDate;
    if (startSrc) {
      const d = new Date(startSrc);
      if (!Number.isNaN(d.getTime())) {
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        setTime(`${hh}:${mm}`);
        return;
      }
    }
    setTime('10:00');
  }, [isOpen, session]);

  const minDate = new Date().toISOString().slice(0, 10);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error('Pick a date and time');
      return;
    }
    // Block past selections client-side too. Server enforces this anyway,
    // but the toast is friendlier than an HTTP 400.
    const picked = new Date(`${date}T${time}:00`);
    if (Number.isNaN(picked.getTime()) || picked.getTime() < Date.now()) {
      toast.error('Pick a future date and time');
      return;
    }
    // Catch an end-before-start here rather than as an HTTP 400. The server rejects
    // it too — this is the friendlier half of the same rule.
    if (endTimeValue && endTimeValue <= time) {
      toast.error('End time must be after the start time');
      return;
    }

    setSubmitting(true);
    try {
      const res = await sessionService.rescheduleSession(session._id, {
        scheduledDate: date,
        startTime: time,
        // Omitted when blank so the server falls back to duration.
        ...(endTimeValue ? { endTime: endTimeValue } : {}),
      });
      if (res?.success) {
        toast.success('Session rescheduled');
        onSuccess?.(res.data);
        onClose();
      } else {
        toast.error(res?.error || 'Failed to reschedule');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to reschedule');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title="Reschedule session"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</>
            ) : (
              <><CalendarClock size={14} className="mr-1.5" /> Reschedule</>
            )}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {session?.name && (
          <div className="rounded-xl bg-neutral-50 ring-1 ring-inset ring-neutral-200/70 px-3 py-2.5 text-sm">
            <p className="font-medium text-neutral-900">{session.name}</p>
            {session.code && (
              <p className="text-xs text-neutral-500 mt-0.5">Code · {session.code}</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">
            New Date <span className="text-rose-500">*</span>
          </label>
          <input
            type="date"
            value={date}
            min={minDate}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full px-3 py-2.5 text-sm rounded-xl bg-white ring-1 ring-inset ring-neutral-200 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">
            New Start Time <span className="text-rose-500">*</span>
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            required
            className="w-full px-3 py-2.5 text-sm rounded-xl bg-white ring-1 ring-inset ring-neutral-200 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1.5">
            New End Time <span className="text-neutral-400 font-normal">(optional)</span>
          </label>
          <input
            type="time"
            value={endTimeValue}
            onChange={(e) => setEndTimeValue(e.target.value)}
            className="w-full px-3 py-2.5 text-sm rounded-xl bg-white ring-1 ring-inset ring-neutral-200 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition"
          />
          <p className="text-xs text-neutral-500 mt-1">
            {endTimeValue
              ? 'The session will end at this time.'
              : `Leave blank and the end time is computed from the existing duration (${session?.duration || 120} min).`}
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default RescheduleSessionModal;
